import {
  Button,
  ButtonGroup,
  Card,
  Position,
  Stack,
  Table,
  Tooltip,
} from "@chaser/components";
import { useState } from "react";
import DeleteUser from "./DeleteUser";
import EmailVerified from "./EmailVerified/index";
import InviteUserModal from "./InviteUserModal";
import useShortCode from "../../../../hooks/useShortCode";
import useUserInfo from "../../../../hooks/useUserInfo";
import useModal from "../../../../hooks/useModal";
import { FeatureGate } from "../../../../components/FeatureGate";
import {
  Features,
  roles,
  AVAILABLE_ROLES,
  hasGovernanceRole,
  Modules,
} from "../../../../util/constants";
import RoleGate, {
  getRoles,
  hasRole,
} from "../../../../components/FeatureGate/RoleGate";
import useCurrentUserRoles from "../../../../hooks/useCurrentUserRoles";
import usePush from "../../../../hooks/usePush";
import AuthenticationService from "../../../../services/authentication-service";
import RoleMultiSelect from "./RoleMultiSelect";
import { Trash2 } from "lucide-react";

const FEATURE_GATE_SYNC_BUFFER_MS = 2000;

const UsersTable = ({ users, refetch, applyOptimisticUpdate }) => {
  const { execute: setRolePermission } = usePush(
    AuthenticationService.setRolePermission,
    {
      errorToast: true,
    },
  );
  const { execute: setInvitedUserRole } = usePush(
    AuthenticationService.setInvitedUserRole,
    {
      errorToast: true,
    },
  );

  const showModal = useModal();
  const { chaserUsers, invitedChaserUsers } = users;
  const shortCode = useShortCode();
  const currentUser = useUserInfo();
  const currentUserRoles = useCurrentUserRoles();

  const isAllowedToChangeRoles = currentUserRoles.canManageUsers;
  const [loadingUsers, setLoadingUsers] = useState(new Set());
  const [pendingUserMutationCount, setPendingUserMutationCount] = useState(0);
  const isUserMutationPending = pendingUserMutationCount > 0;

  const beginUserMutation = () => {
    setPendingUserMutationCount((prev) => prev + 1);
  };

  const endUserMutation = () => {
    window.setTimeout(() => {
      setPendingUserMutationCount((prev) => Math.max(0, prev - 1));
    }, FEATURE_GATE_SYNC_BUFFER_MS);
  };

  async function handleSetRolePermission(
    { userId, email, roles },
    shortCode,
    isVerified,
  ) {
    const userKey = isVerified ? userId : email;
    setLoadingUsers((prev) => new Set(prev).add(userKey));

    const apiAction = isVerified
      ? setRolePermission({ roles, userId }, shortCode)
      : setInvitedUserRole({ roles, email }, shortCode);

    try {
      await applyOptimisticUpdate((state) => {
        if (isVerified) {
          const targetUser = state.chaserUsers.find(
            (user) => user._id === userId,
          );
          if (!targetUser) return;
          if (!targetUser.permissions) targetUser.permissions = {};
          if (!targetUser.permissions.organisation)
            targetUser.permissions.organisation = [];

          let orgPermission = targetUser.permissions.organisation.find(
            (p) => p.orgShortCode === shortCode,
          );

          if (!orgPermission) {
            orgPermission = {
              orgShortCode: shortCode,
              roles: roles,
            };
            targetUser.permissions.organisation.push(orgPermission);
          } else {
            orgPermission.roles = roles;
          }
        } else {
          const targetUser = state.invitedChaserUsers.find(
            (user) => user.email === email,
          );
          if (!targetUser) return;

          targetUser.roles = roles;
        }
      }, apiAction);
    } finally {
      setLoadingUsers((prev) => {
        const next = new Set(prev);
        next.delete(userKey);
        return next;
      });
    }
  }

  const getCurrentRoles = (user) => {
    if (user.verified) {
      const { organisationRoles = [] } = getRoles(user, shortCode);
      return organisationRoles.filter((role) => AVAILABLE_ROLES.includes(role));
    }
    const userRoles = user.roles || [];
    return userRoles.filter((role) => AVAILABLE_ROLES.includes(role));
  };

  const orgColumns = [
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Email",
      accessor: "email",

      cell: (_, user) => <EmailVerified user={user} shortCode={shortCode} />,
    },
    {
      header: "Roles",
      accessor: "roles",
      align: "left",
      headerStyle: { textAlign: "left", width: "45%" },
      cell: (_, user) => {
        const currentRoles = getCurrentRoles(user);
        const targetHasGovernance = hasGovernanceRole(currentRoles);
        const isCurrentUser = currentUser._id === user._id;
        const canChangeRole =
          (currentUserRoles.isStaff ||
            currentUserRoles.isOwner ||
            (currentUserRoles.isManager && !targetHasGovernance)) &&
          !isCurrentUser;
        const userKey = user.verified ? user._id : user.email;
        const isDisabled =
          loadingUsers.has(userKey) ||
          !isAllowedToChangeRoles ||
          !canChangeRole;

        return (
          <RoleMultiSelect
            roles={currentRoles}
            disabled={isDisabled}
            disabledRoles={currentUserRoles.disabledRoles}
            onChange={(newRoles) => {
              handleSetRolePermission(
                { userId: user._id, email: user.email, roles: newRoles },
                shortCode,
                user.verified,
              );
            }}
          />
        );
      },
    },
    {
      header: "",
      align: "right",
      accessor: "action",
      headerStyle: { textAlign: "right", whiteSpace: "nowrap", width: "1%" },
      cell: (_, user) => {
        if (user.verified) {
          if (chaserUsers.length <= 1) return null;
          const ownerCount = chaserUsers.filter(
            (u) => hasRole(u, [roles.owner], shortCode) && u.verified !== false,
          ).length;
          if (user.isOwner && ownerCount <= 1) return null;
          if (currentUser._id === user._id) return null;
        }

        const targetRoles = getCurrentRoles(user);
        const targetHasGovernance = hasGovernanceRole(targetRoles);
        const canDelete =
          currentUserRoles.isStaff ||
          currentUserRoles.isOwner ||
          (currentUserRoles.isManager && !targetHasGovernance);
        if (!canDelete) return null;

        return (
          <RoleGate roles={[roles.owner, roles.manager]}>
            <Button
              data-testid={`delete-invite-btn_${user.email}`}
              data-trackingid={`delete-invite-btn_${user.email}`}
              appearance="secondary"
              intent="danger"
              icon={<Trash2 size={16} />}
              disabled={user.isLoading}
              onClick={() =>
                showModal(
                  <DeleteUser
                    user={user}
                    refetch={refetch}
                    applyOptimisticUpdate={applyOptimisticUpdate}
                    onMutationStart={beginUserMutation}
                    onMutationFinish={endUserMutation}
                  />,
                )
              }
              size="narrow"
            />
          </RoleGate>
        );
      },
    },
  ];

  const chaserUserData = chaserUsers
    ? chaserUsers.map((user) => ({
        _id: user._id,
        permissions: user.permissions,
        isManager: hasRole(user, [roles.manager], shortCode),
        isContributor: hasRole(user, [roles.receivablesContributor], shortCode),
        isViewer: hasRole(user, [roles.receivablesViewer], shortCode),
        isOwner: hasRole(user, [roles.owner], shortCode),
        isBilling: hasRole(user, [roles.billing], shortCode),
        name: `${user.profile.firstName} ${user.profile.lastName}`,
        email: user.emails[0].address || "Missing email",
        verified: true,
      }))
    : [];

  const invitedUserData = invitedChaserUsers
    ? invitedChaserUsers.map((user) => ({
        name: "",
        email: user.email,
        verified: false,
        isBilling: hasRole(
          {
            permissions: {
              organisation: [{ orgShortCode: shortCode, roles: user.roles }],
            },
          },
          [roles.billing],
          shortCode,
        ),
        isContributor: hasRole(
          {
            permissions: {
              organisation: [{ orgShortCode: shortCode, roles: user.roles }],
            },
          },
          [roles.receivablesContributor],
          shortCode,
        ),
        isViewer: hasRole(
          {
            permissions: {
              organisation: [{ orgShortCode: shortCode, roles: user.roles }],
            },
          },
          [roles.receivablesViewer],
          shortCode,
        ),
        isOwner: hasRole(
          {
            permissions: {
              organisation: [{ orgShortCode: shortCode, roles: user.roles }],
            },
          },
          [roles.owner],
          shortCode,
        ),

        roles: user.roles,
        permissions: user.permissions,
        isLoading: user.isLoading,
      }))
    : [];

  const userData = chaserUserData.concat(invitedUserData).sort((a, b) => {
    const emailA = a.email.toUpperCase();
    const emailB = b.email.toUpperCase();
    if (emailA < emailB) return -1;
    if (emailA > emailB) return 1;
    return 0;
  });

  const onInviteUser = () => {
    if (isUserMutationPending) return;

    showModal(
      <InviteUserModal
        refetch={refetch}
        applyOptimisticUpdate={applyOptimisticUpdate}
        onInviteStart={beginUserMutation}
        onInviteFinish={endUserMutation}
      />,
    );
  };

  return (
    <>
      <Stack>
        <Card p="small">
          <Stack>
            <Table
              keyField="email"
              data={userData}
              columns={orgColumns}
              noDataIndication="No users"
            />
            <ButtonGroup>
              <Tooltip
                disable={currentUser.hasVerifiedEmail}
                content="Please verify your email before inviting users"
                position={Position.TOP}
              >
                <RoleGate
                  roles={[roles.owner, roles.manager]}
                  renderGate={(_) => (
                    <Tooltip
                      position={Position.BOTTOM}
                      content="This action needs additional permissions."
                    >
                      <Button
                        appearance="secondary"
                        disabled
                        data-testid="new-invite-user-btn"
                        data-trackingid="new-invite-user-btn"
                      >
                        Invite new user
                      </Button>
                    </Tooltip>
                  )}
                >
                  <FeatureGate
                    name={Features.USERS}
                    onSuccess={onInviteUser}
                    loadingOverlay
                    moduleName={Modules.ORGANISATION}
                  >
                    <Button
                      appearance="secondary"
                      disabled={
                        !currentUser.hasVerifiedEmail || isUserMutationPending
                      }
                      loading={isUserMutationPending}
                      data-testid="new-invite-user-btn"
                      data-trackingid="new-invite-user-btn"
                      onClick={onInviteUser}
                    >
                      Invite new user
                    </Button>
                  </FeatureGate>
                </RoleGate>
              </Tooltip>
            </ButtonGroup>
          </Stack>
        </Card>
        <div style={{ marginTop: "16px" }}>
          <a
            href="https://help.chaserhq.com/manage-users"
            target="_blank"
            rel="noopener noreferrer"
          >
            More information on our knowledgebase: Users
          </a>
        </div>
      </Stack>
    </>
  );
};

export default UsersTable;
